import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Select, Switch, message, Space, Row, Col } from 'antd';
import { BankOutlined, SaveOutlined } from '@ant-design/icons';
import { CompanyService } from '../../services/api/companyService';
import { Company, CompanyUpdate } from '../../types/company';

const { Option } = Select;
const { TextArea } = Input;

interface CompanyProfileDialogProps {
  visible: boolean;
  companyId: string | null;
  onClose: () => void;
  onUpdated?: () => void;
}

export const CompanyProfileDialog: React.FC<CompanyProfileDialogProps> = ({
  visible,
  companyId,
  onClose,
  onUpdated
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [company, setCompany] = useState<Company | null>(null);

  useEffect(() => {
    if (visible && companyId) {
      loadCompany();
    } else if (!visible) {
      // Reset state when modal closes
      setCompany(null);
      // Don't reset form fields here - handleCancel already does it
      // Resetting here when form might not be mounted causes the warning
    }
  }, [visible, companyId]);

  const loadCompany = async () => {
    if (!companyId) return;

    try {
      setLoading(true);
      const companyData = await CompanyService.getById(companyId);
      if (companyData) {
        setCompany(companyData);
        form.setFieldsValue({
          name: companyData.name,
          description: companyData.description,
          industry: companyData.industry,
          size: companyData.size,
          website: companyData.website,
          address: companyData.address,
          phone: companyData.phone,
          email: companyData.email,
          is_active: companyData.is_active,
        });
      } else {
        message.error('Company not found');
        onClose();
      }
    } catch (error: any) {
      console.error('Error loading company:', error);
      message.error('Failed to load company data');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      if (!companyId || !company) {
        message.error('Company not found');
        return;
      }

      const updateData: CompanyUpdate = {
        name: values.name?.trim(),
        description: values.description?.trim(),
        industry: values.industry,
        size: values.size,
        website: values.website?.trim(),
        address: values.address?.trim(),
        phone: values.phone?.trim(),
        email: values.email?.trim(),
        is_active: values.is_active,
      };

      const updatedCompany = await CompanyService.update(companyId, updateData);
      console.log('Company updated successfully:', updatedCompany);
      message.success('Company profile updated successfully');
      
      // Notify parent to refresh company list (this will reload companies and update selectedCompany)
      onUpdated?.();
      
      // Small delay to ensure the refresh completes before closing
      setTimeout(() => {
        onClose();
      }, 100);
    } catch (error: any) {
      console.error('Error updating company:', error);
      message.error(error.message || 'Failed to update company profile');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      title={
        <Space>
          <BankOutlined />
          <span>Company Profile</span>
        </Space>
      }
      open={visible}
      onOk={handleSave}
      onCancel={handleCancel}
      confirmLoading={loading}
      width={700}
      okText="Save"
      okButtonProps={{ icon: <SaveOutlined /> }}
      destroyOnHidden={false}
    >
      <Form
        form={form}
        layout="vertical"
      >
        <Form.Item
          label="Company Name"
          name="name"
          rules={[{ required: true, message: 'Please enter company name' }]}
        >
          <Input placeholder="Enter company name" />
        </Form.Item>

        <Form.Item
          label="Description"
          name="description"
        >
          <TextArea rows={3} placeholder="Enter company description" />
        </Form.Item>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Industry"
              name="industry"
            >
              <Select placeholder="Select industry" allowClear>
                <Option value="Technology">Technology</Option>
                <Option value="Finance">Finance</Option>
                <Option value="Healthcare">Healthcare</Option>
                <Option value="Retail">Retail</Option>
                <Option value="Manufacturing">Manufacturing</Option>
                <Option value="Consulting">Consulting</Option>
                <Option value="HR Technology">HR Technology</Option>
                <Option value="Technology Services">Technology Services</Option>
                <Option value="Software">Software</Option>
                <Option value="Financial Services">Financial Services</Option>
                <Option value="Other">Other</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Company Size"
              name="size"
            >
              <Select placeholder="Select size" allowClear>
                <Option value="Small">Small</Option>
                <Option value="Medium">Medium</Option>
                <Option value="Large">Large</Option>
                <Option value="Enterprise">Enterprise</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          label="Website"
          name="website"
        >
          <Input placeholder="https://example.com" />
        </Form.Item>

        <Form.Item
          label="Email"
          name="email"
          rules={[
            { type: 'email', message: 'Please enter a valid email' }
          ]}
        >
          <Input type="email" placeholder="contact@example.com" />
        </Form.Item>

        <Form.Item
          label="Phone"
          name="phone"
        >
          <Input placeholder="+1 (555) 123-4567" />
        </Form.Item>

        <Form.Item
          label="Address"
          name="address"
        >
          <TextArea rows={2} placeholder="Enter company address" />
        </Form.Item>

        <Form.Item
          label="Status"
          name="is_active"
          valuePropName="checked"
        >
          <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

